import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Button,
  FormInputText,
  Modal,
  Select,
  variables,
} from '@hopskipdrive/react-components';
import { capitalize } from 'lodash';

import { statusOptions } from '../../../constants/statusTypes';

import { Circle, DotContainer, StatusLabel } from './StatusChip.styles';
import { updateStatusValidationSchema } from './validationSchema';

interface IStatusChipProps {
  status: string | undefined;
  employeeId: number | undefined;
  employeeName: string | undefined;
  currentUser?: boolean;
  isLoading: boolean;

  updateStatus: (employeeId, status) => void;
}

type StatusSubmitForm = {
  newStatus: string;
};

const statusBackgroundColorMap = new Map([
  ['online', variables.color.green40],
  ['offline', variables.color.neutral50],
  ['unavailable', variables.color.orange],
  ['error', variables.color.red50],
]);

const statusHoverColorMap = new Map([
  ['online', '#2E7D32'],
  ['offline', variables.color.neutral0],
  ['unavailable', '#ED6C02'],
  ['error', variables.color.red30],
]);

export const StatusChip: FC<IStatusChipProps> = ({
  status,
  employeeId,
  employeeName,
  isLoading,
  updateStatus,
  currentUser = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      {currentUser ? (
        <StatusLabel
          label={capitalize(status)}
          textColor="white"
          bgColor={status && statusBackgroundColorMap.get(status)}
          hoverColor={status && statusHoverColorMap.get(status)}
          type="square"
          size="small"
          onClick={() => setIsOpen(true)}
        />
      ) : (
        <DotContainer onClick={() => setIsOpen(true)}>
          <Circle
            width="13px"
            height="13px"
            bgColor={status && statusBackgroundColorMap.get(status)}
            hoverColor={status && statusHoverColorMap.get(status)}
          />
        </DotContainer>
      )}

      {isOpen && (
        <UpdateStatusModal
          isOpen={isOpen}
          status={status}
          onCancel={() => setIsOpen(false)}
          isLoading={isLoading}
          employeeId={employeeId}
          employeeName={employeeName}
          handleUpdateStatus={(newEmployeeId, newStatus) => {
            updateStatus(newEmployeeId, newStatus);
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
};

const UpdateStatusModal = ({ isOpen, status, onCancel, isLoading, handleUpdateStatus, employeeId, employeeName }) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<StatusSubmitForm>({
    defaultValues: {
      newStatus: '',
    },
    ...(updateStatusValidationSchema && { resolver: yupResolver(updateStatusValidationSchema) }),
  });

  function handleUpdate({ newStatus }) {
    handleUpdateStatus(employeeId, newStatus);
  }

  return (
    <Modal open={isOpen}>
      <Modal.Title>Change Status for {employeeName}</Modal.Title>
      <Modal.Content>
        <form id="update-status-form" onSubmit={handleSubmit(handleUpdate)}>
          <Controller
            control={control}
            name="newStatus"
            render={({ field: { onChange, value } }) => (
              <Select
                options={statusOptions.filter((s) => s.value !== status)}
                value={value}
                setValue={onChange}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    placeholder="Status"
                    fullWidth
                    error={!!errors.newStatus}
                    helperText={errors.newStatus?.message}
                  />
                )}
              />
            )}
          />
        </form>
      </Modal.Content>
      <Modal.Actions>
        <Button variant="text" onClick={onCancel} loading={isLoading}>
          Cancel
        </Button>
        <Button form="update-status-form" type="submit" loading={isLoading}>
          {isLoading ? 'Confirming...' : 'Confirm'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
